import React from 'react';

import moment from 'moment';
import { BringgPopover } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { CustomerAvailabilityHour, DayOfWeek, WayPoint, EndOfWeekDay } from '@bringg/types';

import { RoutesTableTranslations } from '../../../../../routes-planner/translations';

import './customer-column.scss';

enum MomentWeekDays {
	Friday = 4,
	Saturday = 5,
	Sunday = 6,
	Monday = 0,
	Tuesday = 1,
	Wednesday = 2,
	Thursday = 3
}

interface Props {
	wayPoint: WayPoint;
	customerAvailabilityHours: { [customerId: string]: CustomerAvailabilityHour[] };
	translation: RoutesTableTranslations;
	endOfWeekDay: number;
	timeFormat?: string;
}

// key is the value of end day and the value is the value in moment for this day.
// for end day = 0 (first day is friday), the first day is saturday, in moment the value for aturday is 5.
const endOfWeekDayFirstDay: { [key: string]: number } = {
	[EndOfWeekDay.Friday]: MomentWeekDays.Saturday,
	[EndOfWeekDay.Saturday]: MomentWeekDays.Sunday,
	[EndOfWeekDay.Sunday]: MomentWeekDays.Monday,
	[EndOfWeekDay.Monday]: MomentWeekDays.Tuesday,
	[EndOfWeekDay.Tuesday]: MomentWeekDays.Wednesday,
	[EndOfWeekDay.Wednesday]: MomentWeekDays.Thursday,
	[EndOfWeekDay.Thursday]: MomentWeekDays.Friday
};

const CustomerColumn = ({ wayPoint, customerAvailabilityHours, translation, endOfWeekDay, timeFormat }: Props) => {
	const createDaysConverter = () => {
		const daysConverter = {}; // key is the value in moment and value is the new value we shloud use
		Object.entries(endOfWeekDayFirstDay).forEach(([endDayValue, momentValue]) => {
			if (+endDayValue < endOfWeekDay) {
				daysConverter[momentValue] = 7 - (endOfWeekDay - +endDayValue);
			} else if (+endDayValue > endOfWeekDay) {
				daysConverter[momentValue] = +endDayValue - endOfWeekDay;
			} else {
				daysConverter[momentValue] = 0;
			}
		});

		return daysConverter;
	};

	const sortAvailability = (customerAvailabilityHours: CustomerAvailabilityHour[]) => {
		return customerAvailabilityHours.sort(
			(availabilityA, availabilityB) =>
				availabilityA.day_of_week - availabilityB.day_of_week ||
				moment.utc(availabilityA.start_time).hour() - moment.utc(availabilityB.start_time).hour()
		);
	};
	const formatTimeRange = (startTime: string, endTime: string) => {
		const formattedStartTime = moment.utc(startTime).format(timeFormat);
		const formattedEndTime = moment.utc(endTime).format(timeFormat);
		return ` ${formattedStartTime} - ${formattedEndTime}`;
	};

	const buildAvailableHours = (customerAvailabilityHours: CustomerAvailabilityHour[]) => {
		if (customerAvailabilityHours) {
			const hoursByDays: { [key: string]: { day: DayOfWeek; hours: string[] } } = {};
			const orderAvailabilityHoursByDay = sortAvailability(customerAvailabilityHours);
			const dayConverter = createDaysConverter();
			orderAvailabilityHoursByDay.forEach(availability => {
				const dayOfWeek = availability.day_of_week;
				const convertDayOfWeek = dayConverter[dayOfWeek];
				if (hoursByDays[convertDayOfWeek]) {
					hoursByDays[convertDayOfWeek].hours.push(
						formatTimeRange(availability.start_time, availability.end_time)
					);
				} else {
					hoursByDays[convertDayOfWeek] = {
						day: dayOfWeek,
						hours: [formatTimeRange(availability.start_time, availability.end_time)]
					};
				}
			});

			return Object.values(hoursByDays).map(value => {
				const dayOfWeek = moment()
					.isoWeekday(value.day + 1)
					.format('ddd');
				return (
					<div data-test-id="availability-day" className="availability-day">
						<span>{`${dayOfWeek}:`}</span>
						<span data-test-id="availability-hours">{`${value.hours}`}</span>
					</div>
				);
			});
		}
		return [];
	};

	const getCustomerData = () => {
		if (wayPoint.customer) {
			const availabilityHours = buildAvailableHours(customerAvailabilityHours[wayPoint.customer_id]);
			return (
				<div data-test-id="customer-details" className="customer-popover">
					<div className="customer-name">{wayPoint.customer.name}</div>
					{wayPoint.customer.external_id && (
						<div className="customer-id">
							{translation.customerID} {wayPoint.customer.external_id}
						</div>
					)}
					{(wayPoint.phone || wayPoint.customer.phone) && (
						<div>
							<BringgIcon iconName={BringgFontIcons.Phone} className="customer-data-icon" />
							{wayPoint.phone || wayPoint.customer.phone}
						</div>
					)}
					{wayPoint.customer.email && (
						<div>
							<BringgIcon iconName={BringgFontIcons.MessageMail} className="customer-data-icon" />
							{wayPoint.customer.email}
						</div>
					)}
					{availabilityHours.length && customerAvailabilityHours?.[wayPoint.customer_id] ? (
						<div className="customer-availability-hours">
							<span>
								<BringgIcon iconName={BringgFontIcons.Clock} className="customer-data-icon" />
							</span>
							<div>{availabilityHours}</div>
						</div>
					) : null}
				</div>
			);
		}

		return <div />;
	};

	return (
		<BringgPopover trigger={['hover']} content={getCustomerData()}>
			<div data-test-id="customer-name-cell">{wayPoint.customer?.name ?? translation.noCustomerName}</div>
		</BringgPopover>
	);
};

export default CustomerColumn;
